import axios from "axios"

// Same-origin now that the SPA is served by Laravel itself, so a relative
// `/api` path is all that's needed (no more CORS, no more absolute
// VITE_API_URL). Still overridable for edge cases (e.g. previewing the
// built assets against a different API host).
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? "/api",
  withCredentials: true,
  headers: {
    common: {
      "X-Requested-With": "XMLHttpRequest",
    },
  },
})

// Central 401/403 handler: any authenticated admin request that comes back
// unauthorized bounces the user to the login screen. 403 is included
// because it's how EnsureUserIsActive (backend) reports a session whose
// account was deactivated after the token was issued — the token itself is
// still technically valid, so this is the only signal the client gets that
// it needs to drop the session rather than just retry.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      (error?.response?.status === 401 || error?.response?.status === 403) &&
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/admin") &&
      window.location.pathname !== "/admin/login"
    ) {
      window.location.href = "/admin/login"
    }
    return Promise.reject(error)
  },
)

export type ApiEnvelope<T> = { data: T }

export type SocialLinks = {
  facebook?: string | null
  twitter?: string | null
  linkedin?: string | null
  instagram?: string | null
  github?: string | null
}

export type Settings = {
  id: number
  site_name: string
  tagline: string | null
  logo_url: string | null
  favicon_url: string | null
  meta_description: string | null
  seo_title: string | null
  seo_description: string | null
  seo_keywords: string | null
  contact_email: string | null
  contact_phone: string | null
  contact_address: string | null
  map_embed_url: string | null
  social_links: SocialLinks
  about_content: string | null
  mission_statement: string | null
  vision_statement: string | null
  hero_heading: string | null
  hero_subheading: string | null
  hero_cta_text: string | null
  hero_cta_link: string | null
}

export type MenuItem = {
  id: number
  label: string
  url: string
  order: number
  is_active: boolean
  is_external: boolean
}

export type Service = {
  id: number
  icon: string
  title: string
  description: string
  order: number
  is_active: boolean
  features: string[]
  cta_text: string | null
  cta_link: string | null
}

export type Product = {
  id: number
  title: string
  slug: string
  tagline: string | null
  description: string | null
  details_content: string | null
  image_url: string | null
  price_label: string | null
  features: string[]
  cta_text: string | null
  cta_link: string | null
  order: number
  is_active: boolean
}

export type Technology = {
  id: number
  name: string
  logo_url: string | null
  order: number
  is_active: boolean
}

export type PortfolioItem = {
  id: number
  title: string
  description: string
  image_url: string | null
  url: string | null
  order: number
  is_active: boolean
}

export type TeamMember = {
  id: number
  name: string
  designation: string
  photo_url: string | null
  bio: string | null
  social_links: SocialLinks
  order: number
  is_active: boolean
}

export type CompanyValue = {
  id: number
  title: string
  description: string | null
  icon: string | null
  order: number
  is_active: boolean
}

export type Stat = {
  id: number
  label: string
  value: string
  icon: string | null
  order: number
  is_active: boolean
}

export type Testimonial = {
  id: number
  client_name: string
  client_role: string | null
  quote: string
  avatar_url: string | null
  rating: number
  order: number
  is_active: boolean
}

export type BlogPost = {
  id: number
  title: string
  slug: string
  excerpt: string | null
  content: string
  cover_image_url: string | null
  author_name: string | null
  author_role: string | null
  author_avatar_url: string | null
  tags: string[]
  is_published: boolean
  published_at: string | null
  created_at: string | null
  updated_at: string | null
}

export type ContactMessage = {
  id: number
  name: string
  email: string
  subject: string
  message: string
  is_read: boolean
  created_at: string
}

export type DashboardCounts = {
  services: number
  portfolio_items: number
  team_members: number
  testimonials: number
  blog_posts: number
  unread_messages: number
}

export type AdminUser = {
  id: number
  name: string
  email: string
}

export type AppUser = {
  id: number
  name: string
  email: string
  is_active: boolean
  created_at: string | null
}

export type Page = {
  id: number
  title: string
  slug: string
  excerpt: string | null
  content: string | null
  is_published: boolean
  created_at: string | null
  updated_at: string | null
}
